// Slot-map grouping core — the generic primitives behind the
// "studios-style" canvas grouping (flat `{ [nodeId]: 'groupN' }` map +
// `{ [slot]: label }` names), shared by /studio (studios.js) and
// /team-and-agents (team-and-agents.js). Extracted 2026-06-06 per
// Nika ("reuse grouping functionality from /studio") — both pages
// previously carried byte-similar copies of these.
//
// Pages keep their own thin wrappers for page-specific concerns
// (state key names, member ordering, label fallbacks, drag-exclusion
// sets); everything here is shape-agnostic: a slot map is a plain
// object, positions are `{ [id]: { x, y, w, h } }`.

export const GROUP_SLOTS = Array.from({ length: 50 }, (_, i) => 'group' + (i + 1))

// Pick the next unused `groupN` slot for a map. Falls back to a
// timestamp-suffixed slot when all 50 are taken (degenerate but safe).
export const nextFreeSlot = (map) => {
  const used = new Set(Object.values(map || {}))
  for (const slot of GROUP_SLOTS) if (!used.has(slot)) return slot
  return 'group' + Date.now()
}

// Which slot a node id sits in (null = ungrouped).
export const slotOf = (map, nodeId) => (map || {})[nodeId] || null

// Member node-ids of a slot, in map-insertion order. Pages that need
// a domain ordering (people-before-agents, row order, …) re-sort on
// top of this.
export const membersOfSlot = (map, slot) => {
  const out = []
  for (const [id, s] of Object.entries(map || {})) if (s === slot) out.push(id)
  return out
}

// Bounding box around a set of node positions ({x,y,w,h} each).
// `exclude` is an optional Set of ids to skip (e.g. actively-dragged
// nodes so the frame doesn't chase the cursor). Returns null when no
// member has a position.
export const bboxOfMembers = (memberIds, positions, { exclude } = {}) => {
  const items = []
  for (const id of memberIds || []) {
    if (exclude && exclude.has(id)) continue
    const p = positions?.[id]
    if (p) items.push(p)
  }
  if (!items.length) return null
  const minX = Math.min(...items.map((p) => p.x))
  const minY = Math.min(...items.map((p) => p.y))
  const maxX = Math.max(...items.map((p) => p.x + (p.w || 0)))
  const maxY = Math.max(...items.map((p) => p.y + (p.h || 0)))
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

// Re-grid a slot's members in place: top-left anchored at the
// members' current min x/y, `perRow` columns with the given strides.
// Returns a NEW positions object (input untouched).
export const restackSlot = (memberIds, positions, { perRow, colStride, rowStride }) => {
  const next = { ...positions }
  const members = (memberIds || []).filter((id) => next[id])
  if (!members.length) return next
  const minX = Math.min(...members.map((id) => next[id].x))
  const minY = Math.min(...members.map((id) => next[id].y))
  members.forEach((id, i) => {
    const col = i % perRow
    const row = Math.floor(i / perRow)
    next[id] = {
      ...next[id],
      x: minX + col * colStride,
      y: minY + row * rowStride
    }
  })
  return next
}
